import React, { useMemo } from "react";
import FileUploadParserV2 from "./FileUploadParserV2";
import { Button } from "@/components/ui/button";
import { UploadCloud, Paperclip } from "lucide-react";

export type DemoUploadRecord = {
  id: string;
  fileName: string;
  fileType: string;
  wordCount: number;
  uploadedAt: string;
  status: "parsed" | "pending" | "error";
  summary?: string;
};

type UploadPanelProps = {
  uploads: DemoUploadRecord[];
  onUploadRecord: (record: DemoUploadRecord) => void;
  onImportToEditor?: (content: string) => void;
};

const UploadPanel: React.FC<UploadPanelProps> = ({ uploads, onUploadRecord, onImportToEditor }) => {
  const sortedUploads = useMemo(
    () => [...uploads].sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()),
    [uploads]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-2 mb-1">
          <UploadCloud className="w-5 h-5 text-white" />
          <h3 className="font-semibold text-white">Document Uploads</h3>
        </div>
        <p className="text-xs text-white/60">Import research packets and drop excerpts directly into the draft.</p>
      </div>

      <div className="p-4 border-b border-white/10">
        <FileUploadParserV2
          onUploadComplete={(content, fileName, analysis) => {
            const record: DemoUploadRecord = {
              id: `${Date.now()}`,
              fileName,
              fileType: analysis?.fileType || "DOCX",
              wordCount: analysis?.wordCount || content.split(/\s+/).filter(Boolean).length,
              uploadedAt: new Date().toISOString(),
              status: "parsed",
              summary: analysis?.summary || content.slice(0, 120) + (content.length > 120 ? "…" : ""),
            };
            onUploadRecord(record);
            onImportToEditor?.(content);
          }}
        />
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {sortedUploads.length === 0 ? (
          <div className="text-center py-12 text-white/50">
            <Paperclip className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No uploads yet</p>
            <p className="text-xs text-white/40">Drop a file above to populate the research shelf.</p>
          </div>
        ) : (
          sortedUploads.map((upload) => (
            <div
              key={upload.id}
              className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2"
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-white">{upload.fileName}</p>
                  <p className="text-[11px] text-white/50 uppercase tracking-[0.3em]">
                    {upload.fileType} • {upload.wordCount} words
                  </p>
                </div>
                <span className="text-[10px] text-white/40">
                  {new Date(upload.uploadedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
              {upload.summary && (
                <p className="text-xs text-white/70 line-clamp-3">{upload.summary}</p>
              )}
              <div className="flex gap-2 text-xs">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-white/20 text-white/80"
                  onClick={() => onImportToEditor?.(upload.summary || "")}
                >
                  Send to Editor
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-white/60"
                  onClick={() => {
                    if (upload.summary) {
                      navigator.clipboard.writeText(upload.summary).catch(() => {
                        /* noop for demo */
                      });
                    }
                  }}
                >
                  Copy Summary
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default UploadPanel;
